import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { XMarkIcon, CogIcon, ShieldCheckIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import cookieManager from '../services/cookieManager';
import './CookieConsent.css';

const CookieConsent = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false); // Show simple banner first
  const [showDetails, setShowDetails] = useState({});
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = cookieManager.getConsent();
    if (!consent.hasConsent) {
      setIsVisible(true);
      setCookieSettings(consent.settings);
    } else {
      setCookieSettings(consent.settings);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setCookieSettings(allAccepted);
    cookieManager.saveConsent(allAccepted, 'accept_all');
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setCookieSettings(onlyNecessary);
    cookieManager.saveConsent(onlyNecessary, 'reject_all');
    setIsVisible(false);
  };

  const handleSaveSettings = () => {
    cookieManager.saveConsent(cookieSettings, 'granular_choice');
    setIsVisible(false);
  };

  const handleSettingChange = (category, value) => {
    if (category === 'necessary') return; // Cannot change necessary cookies
    setCookieSettings(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleClose = () => {
    // If user closes without making a choice, assume reject all except necessary
    handleRejectAll();
  };

  const toggleDetails = (category) => {
    setShowDetails(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getCookieDetails = () => cookieManager.getCookieDetails();

  // Check if current route is a profile page - hide cookie consent on profile pages
  const isProfilePage = location.pathname.match(/\/[a-z]{2}\/profile\/[A-Za-z0-9]+$/);

  if (!isVisible || isProfilePage) return null;

  return (
    <div className="cookie-consent-overlay">
      <div className="cookie-consent-banner">
        <button
          onClick={handleClose}
          className="cookie-consent-close"
          aria-label={t('cookies.close')}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        {!showSettings ? (
          // Simplified banner - rarely shown since we default to granular
          <div className="cookie-consent-content">
            <div className="cookie-consent-header">
              <ShieldCheckIcon className="h-4 w-4" style={{color: '#d4a574'}} />
              <h2 className="cookie-consent-title">{t('cookies.title')}</h2>
            </div>
            
            <div className="cookie-consent-actions">
              <button
                onClick={handleAcceptAll}
                className="cookie-consent-button cookie-consent-button-accept"
              >
                {t('cookies.acceptAll')}
              </button>
              
              <button
                onClick={handleRejectAll}
                className="cookie-consent-button cookie-consent-button-reject"
              >
                {t('cookies.rejectAll')}
              </button>
              
              <button
                onClick={() => setShowSettings(true)}
                className="cookie-consent-button cookie-consent-button-settings"
              >
                <CogIcon className="h-3 w-3 mr-1" />
                {t('cookies.customize')}
              </button>
            </div>
          </div>
        ) : (
          // Granular consent settings (GDPR compliant)
          <div className="cookie-consent-settings">
            <h2 className="cookie-consent-title">{t('cookies.settingsTitle')}</h2>
            <p className="cookie-consent-description">
              {t('cookies.settingsDescription')}
            </p>

            <div className="cookie-settings-list">
              {/* Necessary cookies */}
              <div className="cookie-setting-item">
                <div className="cookie-setting-header">
                  <div>
                    <h3 className="cookie-setting-name">{t('cookies.necessary.title')}</h3>
                    <button 
                      onClick={() => toggleDetails('necessary')}
                      className="cookie-info-button"
                    >
                      <InformationCircleIcon className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="cookie-setting-toggle">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled={true}
                      className="cookie-toggle-input"
                    />
                    <span className="cookie-toggle-slider cookie-toggle-disabled"></span>
                  </div>
                </div>
                <p className="cookie-setting-description">{t('cookies.necessary.description')}</p>
                {showDetails.necessary && (
                  <div className="cookie-details">
                    {getCookieDetails().necessary.map((cookie, index) => (
                      <div key={index} className="cookie-detail-item">
                        <strong>{cookie.name}</strong>: {cookie.purpose} 
                        <br /><small>{t('cookies.duration')}: {cookie.duration} | {t('cookies.provider')}: {cookie.provider}</small>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Analytics cookies */}
              <div className="cookie-setting-item">
                <div className="cookie-setting-header">
                  <div>
                    <h3 className="cookie-setting-name">{t('cookies.analytics.title')}</h3>
                    <button 
                      onClick={() => toggleDetails('analytics')}
                      className="cookie-info-button"
                    >
                      <InformationCircleIcon className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="cookie-setting-toggle">
                    <input
                      type="checkbox"
                      checked={cookieSettings.analytics}
                      onChange={(e) => handleSettingChange('analytics', e.target.checked)}
                      className="cookie-toggle-input"
                    />
                    <span className="cookie-toggle-slider"></span>
                  </div>
                </div>
                <p className="cookie-setting-description">{t('cookies.analytics.description')}</p>
                {showDetails.analytics && (
                  <div className="cookie-details">
                    {getCookieDetails().analytics.map((cookie, index) => (
                      <div key={index} className="cookie-detail-item">
                        <strong>{cookie.name}</strong>: {cookie.purpose} 
                        <br /><small>{t('cookies.duration')}: {cookie.duration} | {t('cookies.provider')}: {cookie.provider}</small>
                        {cookie.thirdParty && <><br /><small>{t('cookies.thirdParty')}: {cookie.thirdParty}</small></>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Marketing cookies */}
              <div className="cookie-setting-item">
                <div className="cookie-setting-header">
                  <div>
                    <h3 className="cookie-setting-name">{t('cookies.marketing.title')}</h3>
                    <button 
                      onClick={() => toggleDetails('marketing')}
                      className="cookie-info-button"
                    >
                      <InformationCircleIcon className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="cookie-setting-toggle">
                    <input
                      type="checkbox"
                      checked={cookieSettings.marketing}
                      onChange={(e) => handleSettingChange('marketing', e.target.checked)}
                      className="cookie-toggle-input"
                    />
                    <span className="cookie-toggle-slider"></span>
                  </div>
                </div>
                <p className="cookie-setting-description">{t('cookies.marketing.description')}</p>
                {showDetails.marketing && (
                  <div className="cookie-details">
                    {getCookieDetails().marketing.map((cookie, index) => (
                      <div key={index} className="cookie-detail-item">
                        <strong>{cookie.name}</strong>: {cookie.purpose} 
                        <br /><small>{t('cookies.duration')}: {cookie.duration} | {t('cookies.provider')}: {cookie.provider}</small>
                        {cookie.thirdParty && <><br /><small>{t('cookies.thirdParty')}: {cookie.thirdParty}</small></>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Functional cookies */}
              <div className="cookie-setting-item">
                <div className="cookie-setting-header">
                  <div>
                    <h3 className="cookie-setting-name">{t('cookies.functional.title')}</h3>
                    <button 
                      onClick={() => toggleDetails('functional')}
                      className="cookie-info-button"
                    >
                      <InformationCircleIcon className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="cookie-setting-toggle">
                    <input
                      type="checkbox"
                      checked={cookieSettings.functional}
                      onChange={(e) => handleSettingChange('functional', e.target.checked)}
                      className="cookie-toggle-input"
                    />
                    <span className="cookie-toggle-slider"></span>
                  </div>
                </div>
                <p className="cookie-setting-description">{t('cookies.functional.description')}</p>
                {showDetails.functional && (
                  <div className="cookie-details">
                    {getCookieDetails().functional.map((cookie, index) => (
                      <div key={index} className="cookie-detail-item">
                        <strong>{cookie.name}</strong>: {cookie.purpose} 
                        <br /><small>{t('cookies.duration')}: {cookie.duration} | {t('cookies.provider')}: {cookie.provider}</small>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="cookie-consent-actions">
              <button
                onClick={handleSaveSettings}
                className="cookie-consent-button cookie-consent-button-accept"
              >
                {t('cookies.saveSettings')}
              </button>
              
              <button
                onClick={() => setShowSettings(false)}
                className="cookie-consent-button cookie-consent-button-secondary"
              >
                {t('cookies.back')}
              </button>
            </div>
          </div>
        )}
        <div className="cookie-consent-footer">
          <Link to={`/${location.pathname.split('/')[1] || 'mk'}/privacy-policy`} className="cookie-consent-link">
            {t('agePolicy.privacyTitle')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;